import { Grid, Paper, Table, TableBody, TableCell, TableRow } from '@mui/material'
import { AlarmOn, ArrowForward, Error, WarningAmber } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { RoleEnum } from 'store/reducers/user'
import { selectUser } from 'store/selectors/user'
import { getLongFormatDate } from 'utils/dates'
import { renderRichText } from 'utils/helpers'

const RENEWAL_SOON_THRESHOLD = 60 // days

const ROLE_TO_TRANSLATION_KEY = {
  [RoleEnum.LICENSE]: 'license',
  [RoleEnum.ESSENTIAL]: 'essential',
  [RoleEnum.PREMIUM]: 'premium'
}

const getTimeRemainingLabel = (expiry, now, daysLeft, language) => {
  if (expiry <= now) {
    return null
  }

  let years = expiry.getFullYear() - now.getFullYear()
  let months = expiry.getMonth() - now.getMonth()

  if (expiry.getDate() < now.getDate()) {
    months -= 1
  }

  if (months < 0) {
    years -= 1
    months += 12
  }

  const isFrench = language.startsWith('fr')
  const yearLabel = isFrench ? (years === 1 ? 'an' : 'ans') : years === 1 ? 'year' : 'years'
  const monthLabel = isFrench ? 'mois' : months === 1 ? 'month' : 'months'
  const daysLabel = isFrench ? (daysLeft === 1 ? 'jour' : 'jours') : daysLeft === 1 ? 'day' : 'days'

  const parts = []
  if (daysLeft <= RENEWAL_SOON_THRESHOLD) {
    parts.push(`${daysLeft} ${daysLabel}`)
  } else {
    if (years > 0) {
      parts.push(`${years} ${yearLabel}`)
    }
    if (months > 0) {
      parts.push(`${months} ${monthLabel}`)
    }
  }

  return parts.join(isFrench ? ' et ' : ' and ')
}

const UserManagePlan = () => {
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)
  const { organization, role } = useSelector(selectUser)

  const roleLabel = t.user.profile.planTypeValues[ROLE_TO_TRANSLATION_KEY[role]]

  const now = new Date()
  const expiryDate = new Date(organization.expiryDate)
  const daysLeft = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24))
  const isRenewalSoon = daysLeft > 0 && daysLeft <= RENEWAL_SOON_THRESHOLD
  const isExpired = daysLeft <= 0
  const expiryClass = isRenewalSoon ? 'upcoming' : isExpired ? 'expired' : ''

  const showRenewalBanner = isRenewalSoon || isExpired
  const renewalBannerText =
    t.user.profile.renewalBanner[isRenewalSoon ? 'upcoming' : isExpired ? 'expired' : null]
  const RenewalBannerIcon = isRenewalSoon ? WarningAmber : isExpired ? Error : null

  const expiryDateFormatted = getLongFormatDate(organization.expiryDate)
  const timeRemaining = getTimeRemainingLabel(expiryDate, now, daysLeft, i18n.language)
  const timeRemainingLabel = !isExpired
    ? t.user.profile.timeRemaining.replace('{timeRemaining}', timeRemaining)
    : daysLeft === 0
    ? t.user.profile.expiresToday
    : t.user.profile.expired
  const TimeRemainingIcon = isRenewalSoon
    ? AlarmOn
    : isExpired
    ? daysLeft === 0
      ? WarningAmber
      : Error
    : null

  return (
    <Grid className="user-manage-plan">
      <h3>{t.user.profile.managePlanTitle}</h3>
      {showRenewalBanner && (
        <div className={`renewal-banner ${expiryClass}`}>
          {RenewalBannerIcon && <RenewalBannerIcon fontSize="small" />}
          <span>{renewalBannerText}</span>
        </div>
      )}
      <Paper className="current-plan">
        <Grid container className="current-plan-container">
          <Grid className="plan">
            <h4>{t.user.profile.currentPlan}</h4>
            <p className="plan-type">{roleLabel}</p>
          </Grid>
          <Grid className={`plan-expiry ${expiryClass}`}>
            <h4>{t.user.profile.expires}</h4>
            <p className="expiry-date">{expiryDateFormatted}</p>
            <p className="remaining-days">
              {TimeRemainingIcon && <TimeRemainingIcon fontSize="small" />}
              {timeRemainingLabel}
            </p>
          </Grid>
        </Grid>
      </Paper>
      {/* Intentionally hidden for the time being */}
      {false && role != RoleEnum.PREMIUM && (
        <div className="upgrade-banner">
          <span>{renderRichText(t.user.profile.upgradeBanner.blurb)}</span>
          <a href={t.user.profile.upgradeBanner.detailsLink} target="_blank">
            {t.user.profile.upgradeBanner.detailsText}
            <ArrowForward fontSize="small" />
          </a>
        </div>
      )}
      <Table className="plan-details">
        <TableBody>
          <TableRow>
            <TableCell>{t.user.profile.organization}</TableCell>
            <TableCell>{organization.name}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t.user.profile.planType}</TableCell>
            <TableCell>{roleLabel}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t.user.profile.expiresOn}</TableCell>
            <TableCell>{expiryDateFormatted}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Grid>
  )
}

UserManagePlan.tabPath = 'manage-plan'
UserManagePlan.getTabName = t => t.user.profile.managePlanTitle

export default UserManagePlan
